import { useTranslation } from "next-i18next";
import Image from "@/components/Atoms/Image";
import { MultiPurposeParagraph } from "@/components/Atoms/Paragraph";
import {
  ButtonMaster,
  type ButtonMasterVariantProps,
} from "@/components/Atoms/Buttons";
import {
  resolveButtonCustomIcon,
  resolveButtonIconColor,
  resolveProductImage,
} from "@/lib/functions";
import { routerHandler } from "@/lib/routes/router-links-handler";
import styles from "@/components/Organisms/Product/ProductRange/index.module.scss";

type ShowcaseProductCardProps = {
  entry: any;
  locale: string | undefined;
  portfolioSlug: string;
  textColor?: { hex?: string; cssRgb?: string };
  buttonStyle?: ButtonMasterVariantProps["type"];
  buttonConfig?: any;
  brandLabelOverride?: string;
  variant?: "default" | "carousel" | "single";
  carouselImageSize?: "default" | "related";
  showBrandLabel?: boolean;
};

export function ShowcaseProductCard({
  entry,
  locale,
  portfolioSlug,
  textColor,
  buttonStyle = "bgFillSquare",
  buttonConfig,
  brandLabelOverride,
  variant = "default",
  carouselImageSize = "default",
  showBrandLabel = true,
}: ShowcaseProductCardProps) {
  const { t } = useTranslation();
  const isRelatedCarousel =
    variant === "carousel" && carouselImageSize === "related";

  const imageClassName =
    variant === "carousel"
      ? isRelatedCarousel
        ? `z-30 mx-auto ${styles.relatedCarouselProductImage}`
        : "z-30 mx-auto"
      : variant === "single"
      ? "z-30 mx-auto"
      : "max-w-[12rem] md:max-w-[16rem] z-30 mx-auto w-full";

  const imageWrapClassName =
    variant === "carousel"
      ? isRelatedCarousel
        ? styles.pdpRelatedProductImage
        : styles.galleryCarouselProductImageWrap
      : variant === "single"
        ? styles.singleProductImageWrap
        : "flex w-full justify-center";

  const productLink = routerHandler({
    typename: entry?.__typename,
    slug: [portfolioSlug, entry.productTypeSpecific?.slug, entry?.slug],
    locale,
  });
  const brandName = showBrandLabel
    ? brandLabelOverride ??
      entry.customBrandLabel?.label ??
      entry.brandName ??
      entry.productBrand?.title
    : undefined;

  return (
    <div className="flex w-full flex-col items-center">
      <div className={imageWrapClassName}>
        <Image
          className={imageClassName}
          objectFit={
            variant === "carousel" || variant === "single" ? "contain" : undefined
          }
          data={resolveProductImage(entry)}
        />
      </div>
      <article className="flex w-full flex-col items-center gap-6 pt-4">
        <div
          className={`${styles.title} ${variant === "carousel" || variant === "single" ? styles.titleCarousel : ""} flex w-full flex-col items-center gap-2 text-center`}
        >
          {brandName && (
            <MultiPurposeParagraph
              type="showcaseLabel"
              align="center"
              data={brandName}
              color={textColor}
            />
          )}
          {entry.productName && (
            <MultiPurposeParagraph
              type="showcaseProductName"
              align="center"
              data={entry.productName}
              color={textColor}
            />
          )}
        </div>
        {!entry.hideCallToAction && (
          <ButtonMaster
            anim="slideLeft"
            type={buttonConfig?.buttonStyle ?? buttonStyle}
            color={buttonConfig?.labelColor?.color ?? { hex: "#ffffff" }}
            cta={buttonConfig?.typeColor?.color ?? textColor}
            customIcon={resolveButtonCustomIcon(buttonConfig)}
            customColor={resolveButtonIconColor(buttonConfig)}
            link={productLink}
          >
            {buttonConfig?.buttonLabel?.label ?? t("seeMore")}
          </ButtonMaster>
        )}
      </article>
    </div>
  );
}
